import { DonationApi, DonationDetail } from '@/api/donation/donation.model';
import {
  ACCOUNTING_MAX_VALUE_ERROR_MESSAGE,
  ACCOUNTING_SUM_ERROR_MESSAGE,
  CURRENCY_OPTIONS,
} from '@/common/constants';
import { fixAmount } from '@/lib/utils';
import { AccountingEntryItemSchema } from '@/pages/AccountsMayor/AccountingEntry/AccountingEntryItem/AccountingEntryItemSchema';
import { z } from 'zod';
import { DonationFormType } from './DonationSchema';

const getPositiveEntryAmountInDonationCurrency = (
  entry: {
    amount: number;
    currency: string;
    exchangeRateOfficial: number;
  },
  donationCurrency: string,
) => {
  const amount = Math.abs(entry.amount);
  if (entry.amount <= 0) {
    return 0;
  }

  if (entry.currency === donationCurrency) {
    return amount;
  }

  if (donationCurrency === 'ARS') {
    return amount * entry.exchangeRateOfficial;
  }

  if (entry.currency === 'ARS' && entry.exchangeRateOfficial > 0) {
    return amount / entry.exchangeRateOfficial;
  }

  return amount;
};

export const DonationPaySchema = z
  .object({
    paymentDate: z.date({
      required_error: 'Seleccione una fecha de cobro',
    }),
    accountingEntries: z
      .array(AccountingEntryItemSchema)
      .min(2, 'Ingrese al menos dos partidas contables'),
    transactionId: z.string().optional(),
    donationId: z.string().optional(),
    donationCurrency: z.string().optional(),
    donationPendingAmount: z.number().optional(),
  })
  .superRefine((data, ctx) => {
    const balance = fixAmount(
      data.accountingEntries.reduce((acc, entry) => {
        return acc + entry.amount * entry.exchangeRateOfficial;
      }, 0),
      2,
    );

    if (balance !== 0) {
      ctx.addIssue({
        code: 'custom',
        message: ACCOUNTING_SUM_ERROR_MESSAGE,
      });
    }

    data.accountingEntries.forEach((entry, index) => {
      if (entry.category?.id && !entry.costCenter?.id) {
        ctx.addIssue({
          path: ['accountingEntries', index, 'costCenter'],
          message: 'Debe seleccionar un Centro de Costos para la categoría elegida',
          code: 'custom',
        });
      }
    });

    const donationCurrency = data.donationCurrency || 'ARS';
    const totalAmount = fixAmount(
      data.accountingEntries.reduce((acc, entry) => {
        return acc + getPositiveEntryAmountInDonationCurrency(entry, donationCurrency);
      }, 0),
      2,
    );

    if (
      typeof data.donationPendingAmount === 'number' &&
      totalAmount > fixAmount(data.donationPendingAmount, 2)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: ACCOUNTING_MAX_VALUE_ERROR_MESSAGE,
      });
    }
  });

export type DonationPayFormType = z.infer<typeof DonationPaySchema>;

export const defaultDonationPayFormValues: DonationPayFormType = {
  paymentDate: new Date(),
  accountingEntries: [
    {
      global: false,
      ledgerAccount: {
        id: '',
        name: '',
      },
      currency: CURRENCY_OPTIONS[0].value,
      amount: 0,
      exchangeRateOfficial: 1,
      exchangeRateAlt: 1,
      details: '',
      donation: null,
      costCenter: null,
      category: null,
    },
    {
      global: false,
      ledgerAccount: {
        id: '',
        name: '',
      },
      currency: CURRENCY_OPTIONS[0].value,
      amount: 0,
      exchangeRateOfficial: 1,
      exchangeRateAlt: 1,
      details: '',
      donation: null,
      costCenter: null,
      category: null,
    },
  ],
};

export const setDonationDetailDefaultValue = (donation: DonationApi) => {
  if (donation && donation?.donationDetails) {
    const costCentersWithoutGlobal: Array<DonationDetail> = donation?.donationDetails?.filter(
      (centers) => centers?.costCenter?.global !== true,
    );
    const globalCostCenter: DonationDetail = donation?.donationDetails?.find(
      (centers) => centers?.costCenter?.global === true,
    ) as DonationDetail;
    const detailDonationValues: DonationFormType = {
      name: donation?.name,
      fiscalSponsor: {
        id: donation?.fiscalSponsor.id,
        name: donation?.fiscalSponsor.name,
      },
      donor: {
        id: donation?.donor.id,
        name: donation?.donor.name,
      },
      exchangeRate: donation?.money?.exchangeRate || 1,
      amount: donation?.money?.amount || 0,
      description: donation.description || '',
      amountConverted: Number(donation?.amount),
      currency: donation?.money?.currency || 'USD',
      year: donation.year.toString(),
      entryDate: new Date(donation?.entryDate),
      reportDate: new Date(donation?.reportDate),
      files: donation.files,
      globalCostCenter: {
        id: globalCostCenter?.id,
        amount: globalCostCenter?.amount * (donation?.money?.exchangeRate || 1),
      },
      costCenters: costCentersWithoutGlobal?.map((element) => {
        return {
          id: element?.costCenter?.id,
          name: element?.costCenter?.name,
          balance: element?.costCenter.balance,
          categories: element?.costCenter?.balances
            ? element?.costCenter?.balances?.map((category) => {
                return {
                  id: category?.id,
                  name: category?.category,
                  amount: donation.money?.exchangeRate
                    ? category?.amount * donation.money?.exchangeRate
                    : category?.amount,
                };
              })
            : [
                {
                  id: '',
                  amount: 0,
                },
              ],
        };
      }),
    };
    return detailDonationValues;
  }
};
